import React, {useEffect, useRef} from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import {Close} from '@mui/icons-material';
import {SeverityBadge} from './SeverityBadge';

type DefectSeverity = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';

interface AudioDefect {
  id: string;
  title: string;
  description?: string | null;
  severity: DefectSeverity;
  roomType?: string | null;
  defectCategory?: string | null;
  audioStartSeconds?: number | null;
  audioEndSeconds?: number | null;
}

interface AudioPlayerModalProps {
  open: boolean;
  onClose: () => void;
  defect: AudioDefect | null;
  videoUri?: string | null;
}

function formatSeconds(secs: number): string {
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export function AudioPlayerModal({
  open,
  onClose,
  defect,
  videoUri,
}: AudioPlayerModalProps) {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (open && defect?.audioStartSeconds != null && videoRef.current) {
      videoRef.current.currentTime = defect.audioStartSeconds;
    }
  }, [open, defect]);

  if (!defect) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Box flex={1} pr={1}>
            <Typography variant="subtitle1" fontWeight={600}>
              {defect.title}
            </Typography>
            {defect.audioStartSeconds != null && (
              <Typography variant="caption" color="text.secondary">
                {formatSeconds(defect.audioStartSeconds)}
                {defect.audioEndSeconds != null &&
                  ` → ${formatSeconds(defect.audioEndSeconds)}`}
              </Typography>
            )}
          </Box>
          <IconButton size="small" onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Stack direction="row" spacing={1} alignItems="center">
            <SeverityBadge severity={defect.severity} />
            {defect.roomType && (
              <Typography variant="caption" color="text.secondary">
                {defect.roomType}
              </Typography>
            )}
            {defect.defectCategory && (
              <Typography variant="caption" color="text.secondary">
                • {defect.defectCategory}
              </Typography>
            )}
          </Stack>

          {defect.description && (
            <Typography variant="body2" color="text.secondary">
              {defect.description}
            </Typography>
          )}

          {videoUri ? (
            <Box
              component="video"
              ref={videoRef}
              controls
              src={videoUri}
              sx={{width: '100%', borderRadius: 1, maxHeight: 320}}
            />
          ) : (
            <Typography
              variant="body2"
              color="text.secondary"
              fontStyle="italic"
            >
              Video source not available.
            </Typography>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
